import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MapPin,
  Eye,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";
import { CareGiverContext } from "../context/CareGiverContext";
import NotOnboardedMessage from "../components/NotOnboardedMessage";
import { SUBCITIES } from "../utils/constants";

const CareGiverApplicationStatus = () => {
  const { t } = useTranslation();
  const {
    applications,
    fetchApplicationStatus,
    applicationPagination,
    notOnboarded,
    loading,
    getWorkStateLabel,
    getWorkStateColor,
    profileData,
    cToken,
  } = useContext(CareGiverContext);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subcityFilter, setSubcityFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showSubcityDropdown, setShowSubcityDropdown] = useState(false);

  //  pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 8,
  });

  const searchInputRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowStatusDropdown(false);
        setShowSubcityDropdown(false);
      }
    };

    if (showStatusDropdown || showSubcityDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showStatusDropdown, showSubcityDropdown]);

  useEffect(() => {
    // Guard clause: Wait for token to be available before fetching
    if (!cToken) return;
    // Wait for profile to load
    if (!profileData) return;
    // Don't fetch if not onboarded
    if (notOnboarded) return;

    fetchApplicationStatus({
      page: pagination.page,
      limit: pagination.limit,
      status: statusFilter,
      search: searchTerm,
      subcity: subcityFilter,
    });
  }, [
    pagination.page,
    pagination.limit,
    statusFilter,
    searchTerm,
    subcityFilter,
    cToken,
    profileData,
    notOnboarded,
    fetchApplicationStatus,
  ]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowFilters(true);
      } else {
        setShowFilters(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle Enter key press for search
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (
        e.key === "Enter" &&
        document.activeElement === searchInputRef.current
      ) {
        handleSearch();
      }
    };

    document.addEventListener("keypress", handleKeyPress);
    return () => document.removeEventListener("keypress", handleKeyPress);
  }, [tempSearchTerm]);

  // Handle search button click
  const handleSearch = () => {
    const trimmedValue = tempSearchTerm.trim();
    if (trimmedValue !== searchTerm) {
      setSearchTerm(trimmedValue);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setTempSearchTerm("");
    if (searchTerm !== "") {
      setSearchTerm("");
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    setStatusFilter("all");
    setSubcityFilter("all");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle subcity filter change
  const handleSubcityChange = (value) => {
    setSubcityFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setPagination({
      page: 1,
      limit: Number(limit),
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  // Toggle dropdown for details
  const toggleDropdown = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Render status with icon and text
  const renderStatusWithIconAndText = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return (
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
              {t("status.statusPending")}
            </span>
          </div>
        );
      case "under-review":
        return (
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
              {t("status.statusUnderReview")}
            </span>
          </div>
        );
      case "accepted":
        return (
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm font-medium text-green-700 dark:text-green-400">
              {t("status.statusAccepted")}
            </span>
          </div>
        );
      case "rejected":
        return (
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium text-red-700 dark:text-red-400">
              {t("status.statusRejected")}
            </span>
          </div>
        );
      case "withdrawn":
        return (
          <div className="flex items-center gap-2">
            <X className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-400">
              {t("status.statusWithdrawn")}
            </span>
          </div>
        );
      default:
        return <span className="text-sm font-medium">{status}</span>;
    }
  };

  // Render category-specific fields
  const renderCategorySpecificFields = (job) => {
    if (!job) return null;

    const fields = [];

    // Common fields for all categories
    if (job.category) {
      fields.push(
        <div key="category" className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
            {t("modals.jobDetail.category")}:
          </span>
          <span className="text-sm font-medium capitalize">{job.category}</span>
        </div>,
      );
    }

    if (job.experienceLevel) {
      fields.push(
        <div key="experience" className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
            {t("modals.jobDetail.experience")}:
          </span>
          <span className="text-sm font-medium">{job.experienceLevel}</span>
        </div>,
      );
    }

    // Category-specific fields
    switch (job.category) {
      case "childcare":
        if (job.age_groups?.length > 0) {
          fields.push(
            <div key="age_groups" className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                {t("modals.jobDetail.agesCaredFor")}:
              </span>
              <span className="text-sm font-medium">
                {job.age_groups.join(", ")}
              </span>
            </div>,
          );
        }
        break;
      case "cook":
        if (job.service_frequency) {
          fields.push(
            <div key="service_frequency" className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                {t("modals.jobDetail.daysPerWeek")}:
              </span>
              <span className="text-sm font-medium">
                {job.service_frequency}{" "}
                {t("modals.jobDetail.daysPerWeek") || "days/week"}
              </span>
            </div>,
          );
        }
        if (job.meal_preparation) {
          fields.push(
            <div key="meal_preparation" className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                {t("modals.jobDetail.mealPreparation")}:
              </span>
              <span className="text-sm font-medium">
                {job.meal_preparation}
              </span>
            </div>,
          );
        }
        break;
      case "cleaner":
        if (job.room_numbers) {
          fields.push(
            <div key="room_numbers" className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                {t("modals.jobDetail.roomsToClean")}:
              </span>
              <span className="text-sm font-medium">{job.room_numbers}</span>
            </div>,
          );
        }
        if (job.light_housekeeping) {
          fields.push(
            <div key="light_housekeeping" className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                {t("modals.jobDetail.lightHousekeeping")}:
              </span>
              <span className="text-sm font-medium">
                {job.light_housekeeping}
              </span>
            </div>,
          );
        }
        break;
      case "homeTutor":
        if (job.teaching_levels?.length > 0) {
          fields.push(
            <div key="teaching_levels" className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                {t("modals.jobDetail.studentLevels")}:
              </span>
              <span className="text-sm font-medium">
                {job.teaching_levels.join(", ")}
              </span>
            </div>,
          );
        }
        if (job.hours_per_day) {
          fields.push(
            <div key="hours_per_day" className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                {t("modals.jobDetail.hoursPerDay")}:
              </span>
              <span className="text-sm font-medium">{job.hours_per_day}</span>
            </div>,
          );
        }
        break;
      case "elderlycare": {
        const elderlyFields = [
          {
            key: "elderly_care_experience",
            label:
              t("modals.jobDetail.careServicesNeeded") ||
              "Elderly Care Experience",
          },
          {
            key: "first_aid_cpr_certified",
            label:
              t("modals.jobDetail.firstAidRequired") ||
              "First Aid/CPR Certified",
          },
          {
            key: "transportation_assistance",
            label:
              t("modals.jobDetail.transportation") ||
              "Transportation Assistance",
          },
          {
            key: "medication_management",
            label:
              t("modals.jobDetail.medicationHelp") || "Medication Management",
          },
          {
            key: "mobility_assistance",
            label:
              t("modals.jobDetail.mobilityAssistance") || "Mobility Assistance",
          },
          {
            key: "companionship",
            label: t("modals.jobDetail.companionship") || "Companionship",
          },
        ];

        elderlyFields.forEach((field) => {
          if (job[field.key]) {
            fields.push(
              <div key={field.key} className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                  {field.label}:
                </span>
                <span className="text-sm font-medium">{job[field.key]}</span>
              </div>,
            );
          }
        });
        break;
      }
      default:
        break;
    }

    return fields.length > 0 ? (
      <div className="space-y-2">{fields}</div>
    ) : (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {t("status.noAdditionalDetails")}
      </div>
    );
  };

  // Show spinner while profile is loading
  if (!profileData) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-500 dark:text-dark-muted">
          {t("status.loading")}
        </p>
      </div>
    );
  }

  // Early return for notOnboarded
  if (notOnboarded) {
    return <NotOnboardedMessage featureName="application status" />;
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            {t("status.title")}
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm">
            {t("status.subtitle")}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {t("status.showingOf", {
              count: applications.length,
              total: applicationPagination.total,
            })}
          </p>
        </div>

        {/* Filter Toggle Button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-card dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-fit"
        >
          <Filter size={20} className="text-primary" />
          {showFilters ? t("status.hideFilters") : t("status.showFilters")}
        </button>
      </div>

      {/* Filter Toolbar */}
      {showFilters && (
        <div className="bg-card dark:bg-dark-surface p-4 rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border">
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t("status.searchPlaceholder")}
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                className="w-full pl-12 pr-20 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium transition-all"
              />

              {/* Search Icon Button */}
              {tempSearchTerm.trim() && (
                <button
                  onClick={handleSearch}
                  className="absolute right-12 top-1/2 -translate-y-1/2 p-2 text-primary hover:text-primary-dark transition-colors"
                  title="Search"
                >
                  <Search size={18} />
                </button>
              )}

              {/* Clear Button  */}
              {tempSearchTerm.trim() && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 lg:gap-0">
              <div className="relative min-w-[180px] dropdown-container">
                <button
                  type="button"
                  onClick={() => {
                    setShowStatusDropdown(!showStatusDropdown);
                    setShowSubcityDropdown(false);
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <span>
                    {statusFilter === "all"
                      ? t("status.allStatuses")
                      : statusFilter === "pending"
                        ? t("status.statusPending")
                        : statusFilter === "under-review"
                          ? t("status.statusUnderReview")
                          : statusFilter === "accepted"
                            ? t("status.statusAccepted")
                            : statusFilter === "rejected"
                              ? t("status.statusRejected")
                              : t("status.statusWithdrawn")}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-primary transition-transform ${
                      showStatusDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showStatusDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                    {[
                      { value: "all", label: t("status.allStatuses") },
                      { value: "pending", label: t("status.statusPending") },
                      {
                        value: "under-review",
                        label: t("status.statusUnderReview"),
                      },
                      { value: "accepted", label: t("status.statusAccepted") },
                      { value: "rejected", label: t("status.statusRejected") },
                      {
                        value: "withdrawn",
                        label: t("status.statusWithdrawn"),
                      },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          handleStatusChange(option.value);
                          setShowStatusDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          statusFilter === option.value
                            ? "bg-primary text-white dark:bg-primary"
                            : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative min-w-[180px] lg:ml-3 dropdown-container">
                <button
                  type="button"
                  onClick={() => {
                    setShowSubcityDropdown(!showSubcityDropdown);
                    setShowStatusDropdown(false);
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <span>
                    {subcityFilter === "all"
                      ? t("status.allSubcities")
                      : subcityFilter}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-primary transition-transform ${
                      showSubcityDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showSubcityDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                    <button
                      onClick={() => {
                        handleSubcityChange("all");
                        setShowSubcityDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        subcityFilter === "all"
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {t("status.allSubcities")}
                    </button>
                    {SUBCITIES.map((subcity) => (
                      <button
                        key={subcity}
                        onClick={() => {
                          handleSubcityChange(subcity);
                          setShowSubcityDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          subcityFilter === subcity
                            ? "bg-primary text-white dark:bg-primary"
                            : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                        }`}
                      >
                        {subcity}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset Button */}
              <button
                onClick={resetFilters}
                className="px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 text-text dark:text-dark-text font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors lg:ml-3 flex items-center justify-center gap-2"
              >
                {t("status.resetAll")}
              </button>
            </div>
          </div>

          {/* Search Info Text */}
          <div className="mt-3 text-xs text-gray-500 dark:text-dark-muted">
            <p>{t("status.searchHint")}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-dark-muted">
            {t("status.loading")}
          </p>
        </div>
      )}

      {!loading && (
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
          <div className="overflow-x-auto -mx-4 md:mx-0">
            <div className="min-w-[768px] md:min-w-full">
              <div className="overflow-visible min-h-[400px]">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("status.colJobDesc")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("status.colClientId")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("status.colLocation")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("status.colWorkTime")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("status.colStatus")}
                      </th>
                      <th className="px-4 md:px-6 py-5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {t("status.colDetails")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {applications.length > 0 ? (
                      applications.map((application) => (
                        <React.Fragment key={application.id}>
                          <tr className="group hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                            <td className="px-4 md:px-6 py-4">
                              <div className="max-w-xs">
                                <div className="font-medium text-text dark:text-dark-text text-sm truncate">
                                  {application.job?.description ||
                                    t("requests.noDescription")}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                                  {t("status.expLabel")}{" "}
                                  {application.job?.experienceLevel || "N/A"}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="text-sm text-text dark:text-dark-text font-medium">
                                {typeof application.job?.created_by === "object"
                                  ? application.job?.created_by?.name || "N/A"
                                  : application.job?.created_by
                                    ? `User: ${application.job.created_by.toString().slice(-6)}`
                                    : "N/A"}
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="space-y-1">
                                <div className="text-sm text-text dark:text-dark-text font-medium">
                                  {application.job?.subcity || "N/A"}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-dark-muted">
                                  {application.job?.location || "N/A"}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="text-sm text-text dark:text-dark-text">
                                {application.job?.workTime || "N/A"}
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              {renderStatusWithIconAndText(application.status)}
                            </td>
                            <td className="px-4 md:px-6 py-4 text-center">
                              <button
                                onClick={() => toggleDropdown(application.id)}
                                className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors mx-auto"
                              >
                                {expandedRow === application.id ? (
                                  <ChevronUp size={18} />
                                ) : (
                                  <ChevronDown size={18} />
                                )}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Details Row */}
                          {expandedRow === application.id && (
                            <tr className="bg-gray-50/50 dark:bg-gray-800/50">
                              <td colSpan="6" className="px-4 md:px-6 py-4">
                                <div className="bg-white dark:bg-gray-900 rounded-xl p-4 md:p-6 border border-gray-100 dark:border-gray-800">
                                  <div className="flex items-center gap-2 mb-4">
                                    <AlertCircle className="w-5 h-5 text-primary" />
                                    <h4 className="font-semibold text-text dark:text-dark-text">
                                      {t("status.jobDetailsTitle")}
                                    </h4>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {renderCategorySpecificFields(
                                      application.job,
                                    )}
                                  </div>

                                  {/* WorkState Display for Accepted Jobs */}
                                  {application.status === "accepted" &&
                                    application.job?.workState && (
                                      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                                        <div className="flex items-center gap-2 mb-2">
                                          <AlertCircle className="w-4 h-4 text-primary" />
                                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            {t("status.jobStatusLabel")}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={`px-3 py-1 rounded-lg text-xs font-bold ${getWorkStateColor(application.job.workState)}`}
                                          >
                                            {getWorkStateLabel(
                                              application.job.workState,
                                            )}
                                          </span>
                                          {application.job.workState ===
                                            "work-started" && (
                                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                                              {t("status.workInProgress")}
                                            </span>
                                          )}
                                          {application.job.workState ===
                                            "completed" && (
                                            <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                              {t("status.jobCompleted")}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                  {application.createdAt && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                        <Clock size={14} />
                                        {t("status.appliedOn")}{" "}
                                        {new Date(
                                          application.createdAt,
                                        ).toLocaleDateString()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 md:px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                        >
                          {t("status.noHistory")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination Component */}
          <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
            <Pagination
              currentPage={pagination.page}
              totalPages={applicationPagination.totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={pagination.limit}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CareGiverApplicationStatus;
